import { useMemo } from 'react';
import { useFiltersStore } from '../stores/appFiltersStore';

export interface RevenueFilters {
  revenueCategories: string[];
  /** Source NAMES (see appFiltersStore.revenueSourceFilters). Empty = every source. */
  revenueSources: string[];
  menuCategories: string[];
}

export interface UseRevenueFiltersConfig {
  includeRevenueCategories?: boolean;
  includeRevenueSources?: boolean;
  includeMenuCategories?: boolean;
}

export interface UseRevenueFiltersReturn {
  filters: RevenueFilters;
  setRevenueCategoryFilters?: (filters: string[]) => void;
  setRevenueSourceFilters?: (filters: string[]) => void;
  setMenuCategoryFilters?: (filters: string[]) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
  activeFilterCount: number;
}

/**
 * Hook for managing revenue history filters
 * Returns current filter state and setters for updating filters
 * @param config - Optional configuration to include specific filters
 */
export function useRevenueFilters(config: UseRevenueFiltersConfig = {
  includeRevenueCategories: true,
  includeRevenueSources: true,
  includeMenuCategories: true,
}): UseRevenueFiltersReturn {
  const {
    includeRevenueCategories = true,
    includeRevenueSources = true,
    includeMenuCategories = true,
  } = config;

  const revenueCategoryFilters = useFiltersStore(s => s.revenueCategoryFilters);
  const setRevenueCategoryFilters = useFiltersStore(s => s.setRevenueCategoryFilters);
  const revenueSourceFilters = useFiltersStore(s => s.revenueSourceFilters);
  const setRevenueSourceFilters = useFiltersStore(s => s.setRevenueSourceFilters);
  const menuCategoryFilters = useFiltersStore(s => s.menuCategoryFilters);
  const setMenuCategoryFilters = useFiltersStore(s => s.setMenuCategoryFilters);
  const resetRevenueFilters = useFiltersStore(s => s.resetRevenueFilters);

  const filters: RevenueFilters = useMemo(() => ({
    revenueCategories: includeRevenueCategories ? revenueCategoryFilters : [],
    revenueSources: includeRevenueSources ? revenueSourceFilters : [],
    menuCategories: includeMenuCategories ? menuCategoryFilters : [],
  }), [revenueCategoryFilters, revenueSourceFilters, menuCategoryFilters, includeRevenueCategories, includeRevenueSources, includeMenuCategories]);

  const hasActiveFilters = useMemo(() => {
    return (includeRevenueCategories && revenueCategoryFilters.length > 0) ||
           (includeRevenueSources && revenueSourceFilters.length > 0) ||
           (includeMenuCategories && menuCategoryFilters.length > 0);
  }, [revenueCategoryFilters, revenueSourceFilters, menuCategoryFilters, includeRevenueCategories, includeRevenueSources, includeMenuCategories]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (includeRevenueCategories && revenueCategoryFilters.length > 0) count++;
    if (includeRevenueSources && revenueSourceFilters.length > 0) count++;
    if (includeMenuCategories && menuCategoryFilters.length > 0) count++;
    return count;
  }, [revenueCategoryFilters, revenueSourceFilters, menuCategoryFilters, includeRevenueCategories, includeRevenueSources, includeMenuCategories]);

  return {
    filters,
    setRevenueCategoryFilters: includeRevenueCategories ? setRevenueCategoryFilters : undefined,
    setRevenueSourceFilters: includeRevenueSources ? setRevenueSourceFilters : undefined,
    setMenuCategoryFilters: includeMenuCategories ? setMenuCategoryFilters : undefined,
    resetFilters: resetRevenueFilters,
    hasActiveFilters,
    activeFilterCount,
  };
}
